import { motion } from "framer-motion"
import type { ReactNode } from "react"
import { fadeUp } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

/** Блок редактора. Заголовок и поля внутри стоят на одной сетке отступов. */
export function EditorSection({
	id,
	title,
	description,
	aside,
	children,
	className,
}: {
	id?: string
	title: string
	description?: string
	/** Что-то справа от заголовка: счётчик, кнопка. */
	aside?: ReactNode
	children: ReactNode
	className?: string
}) {
	return (
		<motion.section
			id={id}
			variants={fadeUp}
			className={cn("scroll-mt-24 rounded-2xl border border-border bg-card p-5 sm:p-6", className)}
		>
			<header className="mb-5 flex items-start justify-between gap-4">
				<div className="min-w-0">
					<h2 className="text-base font-semibold tracking-tight">{title}</h2>
					{description ? (
						<p className="mt-1 text-sm leading-snug text-muted-foreground">{description}</p>
					) : null}
				</div>
				{aside}
			</header>
			<div className="space-y-6">{children}</div>
		</motion.section>
	)
}

/**
 * Поле редактора: подпись, контрол, строка подсказки со счётчиком. Отступы одни на всю
 * форму: 8 px от подписи до поля и 6 px до подсказки.
 */
export function EditorField({
	label,
	htmlFor,
	required,
	hint,
	error,
	count,
	children,
}: {
	label: string
	htmlFor?: string
	required?: boolean
	hint?: ReactNode
	error?: string
	count?: { value: number; max: number }
	children: ReactNode
}) {
	const over = count ? count.value > count.max : false
	return (
		<div>
			<label htmlFor={htmlFor} className="mb-2 flex items-baseline gap-1 text-sm font-medium">
				{label}
				{required ? (
					<span aria-hidden className="text-primary">
						*
					</span>
				) : null}
			</label>
			{children}
			{error || hint || count ? (
				<div className="mt-1.5 flex items-start justify-between gap-3 text-xs leading-snug">
					<p className={error ? "text-destructive" : "text-muted-foreground"} role={error ? "alert" : undefined}>
						{error ?? hint}
					</p>
					{count ? (
						<span
							className={cn(
								"shrink-0 tabular-nums",
								over
									? "text-destructive"
									: count.value > count.max * 0.9
										? "text-amber-300"
										: "text-muted-foreground",
							)}
						>
							{count.value}/{count.max}
						</span>
					) : null}
				</div>
			) : null}
		</div>
	)
}
